<script lang="ts" module>
	import type { Snippet } from 'svelte';
	import type { ClassValue, SvelteHTMLElements } from 'svelte/elements';
	import type { VariantProps } from 'tailwind-variants';
	import { tv } from '../tv';
	import { MAIN_COLOR_VARIANTS, RADII_VARIANTS } from '../variants';

	export const badgeVariants = tv({
		base: 'inline-flex shrink-0 items-center justify-center focus-outline disabled-state',
		variants: {
			size: {
				'500': 'h-[1.25rem] min-w-[1.25rem] px-s200',
				'400': 'h-[1rem] min-w-[1rem] px-s100',
				'300': 'h-[0.75rem] min-w-[0.75rem] px-s100',
				'200': 'h-[0.5rem] min-w-[0.5rem] px-s100'
			},
			variant: MAIN_COLOR_VARIANTS,
			fill: {
				Solid: 'border-(--v-main-line) bg-(--v-main) text-(--v-on-main) outline-(--v-main-line)',
				Soft: 'border-(--v-container-line) bg-(--v-container) text-(--v-on-container) outline-(--v-container-line)',
				None: 'border-(--v-main) bg-inherit text-(--v-main) outline-(--v-main)'
			},
			outlined: { true: 'outline-1 outline-solid' },
			radii: RADII_VARIANTS
		},
		defaultVariants: { size: '400', variant: 'Primary', fill: 'Soft', radii: '300' }
	});

	export type BadgeProps<T extends keyof SvelteHTMLElements = 'span'> = Omit<
		SvelteHTMLElements[T],
		'class'
	> &
		VariantProps<typeof badgeVariants> & {
			as?: T;
			children?: Snippet;
			class?: ClassValue;
			ref?: HTMLElement | null;
		};
</script>

<script lang="ts" generics="T extends keyof SvelteHTMLElements = 'span'">
	let {
		as = 'span' as T,
		children,
		class: className,
		fill,
		outlined,
		radii,
		ref = $bindable(null),
		size,
		variant,
		...rest
	}: BadgeProps<T> = $props();
</script>

<svelte:element
	this={as}
	bind:this={ref}
	class={[badgeVariants({ fill, outlined, radii, size, variant }), className]}
	{...rest}
>
	{@render children?.()}
</svelte:element>
